/**
 * PF v2 file hub — provider/hook factory.
 * ============================================================================
 *
 * `createFileHubKind(kind)` returns a `constate` `[Provider, useKind]` pair that
 * owns a single `useFileAPI(kind.scope)` and exposes the kind's upload / delete
 * ops + projected read model. Generalized (no logic change) from the old
 * staging-specific `PfV2VisualUpload.context.tsx`; the only real parameterization
 * is gating the post-upload derivative poll on `kind.derivatives?.length`.
 *
 * **Single-provider rule** (load-bearing): `useFileAPI`'s `INIT_SCOPE` is
 * destructive (wipes `state[scope]` on every mount, not merge), so a scope
 * shared across mount points needs exactly ONE `useFileAPI(scope)` consumer.
 * Mounting this factory's `Provider` once at the nearest common ancestor and
 * reading `useKind()` in each child enforces that by construction (mirrors the
 * Gallery `GalleryVisualsFileAPIProvider` pattern).
 *
 * The factory reads `sessionId` / `email` live from the PF-v2 store — that is
 * the transport context every PF-v2 kind shares; a kind never touches the store
 * itself (A-04: the hub is storage-agnostic, no `productConfigs` write-back).
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import constate from 'constate'
import { useTranslation } from 'react-i18next'

import { FileAction, FileState, useFileAPI } from 'components/common/FileAPI'
import type { FileAPIEntry } from 'components/common/FileAPI'
import { useSnackbar } from 'components/contexts/SnackbarService.context'
import type { RecursivePartial } from 'models/helpers'
import { logger } from 'utils/logging'

import { usePfStore } from '../store'
import { resolveVisualDerivative } from './fileHub.transport'
import type { FileHubBaseMetadata, FileHubContext, FileHubKind } from './fileHub.types'

/** A PDF is stored as it is: the backend renders no preview of it, so there is nothing to poll for. */
const hasNoDerivative = (filename: string) => /\.pdf$/i.test(filename)

export function createFileHubKind<
  Metadata extends FileHubBaseMetadata,
  Projected,
  UploadParams = void
>(kind: FileHubKind<Metadata, Projected, UploadParams>) {
  return constate(() => {
    const fileApi = useFileAPI<Metadata>(kind.scope, { purgeOnDestroy: kind.purgeOnDestroy })
    const { t } = useTranslation(['purchase_flow_revamp'])
    const { spawnWarningToast } = useSnackbar()

    // Live getters — resolvers read session/workspace/email at request time so a
    // late-resolving session never bakes a stale value into a closure.
    const getSessionId = useCallback(() => usePfStore.getState().draft.sessionId, [])
    // The session's OWN workspace, not the effective one: the v2 session-file
    // endpoints are addressed by both ids and the backend 403s if they disagree.
    const getWorkspaceId = useCallback(() => usePfStore.getState().draft.sessionWorkspaceId, [])
    const getEmail = useCallback(() => usePfStore.getState().targetUserEmail, [])
    const ctx = useMemo<FileHubContext>(
      () => ({ getSessionId, getWorkspaceId, getEmail }),
      [getSessionId, getWorkspaceId, getEmail]
    )

    // FileAPI handle fns are recreated every render. Ref-mirror `editFile`
    // so the async `onSettled` derivative callback always hits the current one.
    const editFileRef = useRef(fileApi.editFile)
    editFileRef.current = fileApi.editFile

    // Files whose derivative is already being resolved, shared by the upload callback and the resolve
    // pass below so a fresh upload is polled once, not twice.
    const resolvingRef = useRef<Set<string>>(new Set())

    // ── Session-scoped "seen in the feed" id set (§10.2 async-membership) ──────
    //
    // Accumulates every file id that has APPEARED in this scope's feed at any point
    // during the current session. It powers the staging room-set reconcile
    // (`useStagingRoomSync`): a module prunes a DTO item ONLY if its id was seen
    // live this session and is now absent from the feed — so a genuine drawer/
    // catalogue delete prunes its room, while a hydrated-on-resume item (never seen
    // this session; feed empty on resume) is IMMUNE → no F-B1 clobber on the
    // collection axis. Provider-owned (this constate is mounted ONCE at the PF-v2
    // root) → shared across the drawer, the catalogue, and the config step, which a
    // per-consumer ref could not achieve. Reset on session change (resume/regen) so
    // a resumed order starts fresh. Accumulate-only within a session; value-guarded
    // so an unchanged feed produces no new Set reference (no churn).
    const sessionId = usePfStore((s) => s.draft.sessionId)
    const [seenThisSession, setSeenThisSession] = useState<Set<string>>(() => new Set())

    // Held here rather than in the upload area, so an upload that settles after its page was left is still recorded.
    const [touchedTags, setTouchedTags] = useState<ReadonlySet<string>>(() => new Set())
    const markTouched = useCallback((tag = '') => {
      setTouchedTags((prev) => (prev.has(tag) ? prev : new Set(prev).add(tag)))
    }, [])

    const prevSessionRef = useRef(sessionId)
    useEffect(() => {
      if (prevSessionRef.current === sessionId) return
      prevSessionRef.current = sessionId
      setSeenThisSession(new Set())
      setTouchedTags(new Set())
    }, [sessionId])

    useEffect(() => {
      setSeenThisSession((prev) => {
        let next: Set<string> | null = null
        for (const entry of fileApi.allFilesArray) {
          const id = entry.gcFilename ?? entry.id
          if (!prev.has(id)) (next ??= new Set(prev)).add(id)
        }
        return next ?? prev
      })
    }, [fileApi.allFilesArray])

    // A file the backend reported as gone is already deleted server-side; showing it would claim a
    // document the order no longer has.
    const goneSessionFiles = usePfStore((s) => s.goneSessionFiles)
    useEffect(() => {
      if (goneSessionFiles.length === 0) return
      const goneIds = fileApi.allFilesArray
        .filter((entry) => entry.gcFilename && goneSessionFiles.includes(entry.gcFilename))
        .map((entry) => entry.id)
      if (goneIds.length > 0) fileApi.deleteFiles(goneIds, { localOnly: true })
    }, [goneSessionFiles, fileApi])

    /** Eagerly upload dropped files, then either poll each declared derivative
     *  and stamp the entry ready (visual kinds), or — for plain-upload kinds —
     *  stamp ready immediately on settle. */
    const upload = useCallback((files: File[] | FileList, params: UploadParams) => {
      const derivatives = kind.derivatives
      fileApi.uploadFiles(files, {
        tag: kind.resolveTag?.(params),
        metadata: kind.resolveUploadMetadata?.(params),
        uploadUrlResolverFnc: kind.makeUploadResolver(ctx, params),
        // Removed while its URL was being minted: the upload never starts, but the session already
        // holds the reserved name, so it is deleted like any other removed file.
        onDiscardedAfterMint: (entries, api) => {
          const deleteFile = kind.makeDeleteHandler(ctx)
          for (const entry of entries) {
            deleteFile(entry, api).catch((error) => logger.error('PF revamp: deleting a file removed during upload failed', error, {
              context: { component: 'FileHub', action: 'onDiscardedAfterMint', metadata: { gcFilename: entry.gcFilename } },
            }))
          }
        },
        // `errorIds` is defaulted rather than trusted: a caller that omits it would
        // otherwise throw in here and take the derivative stamping down with it.
        onSettled: (successIds, errorIds = []) => {
          // A file whose upload never completed has no stored object and no
          // server-assigned name, so there is nothing for a delete to address:
          // leaving it in the listing produced a chip whose remove button could
          // only ever 404, i.e. an entry the user could not get rid of. Drop those
          // entries locally and report the failure instead — the error belongs in a
          // message, not in a permanent row. Cancellations are deliberate and are
          // left out of the count.
          if (errorIds.length) {
            fileApi.deleteFiles(errorIds, { localOnly: true })
            spawnWarningToast(t('purchase_flow_revamp:upload_failed', { count: errorIds.length }))
          }

          if (derivatives?.length) {
            const sessionId = getSessionId()
            if (!sessionId) return

            // N1 hardening: only the DISPLAY derivative writes `displayUrl`.
            // With 2+ derivatives, letting each write the same `displayUrl` on
            // the same entry races → last writer clobbers. Non-display
            // derivatives are polled for readiness only (BE pre-warm). Defaults
            // to the first derivative, so single-derivative kinds (staging) are
            // unaffected — their only derivative IS the display derivative.
            const displayDerivative = kind.displayDerivative ?? derivatives[0]

            for (const id of successIds) {
              const isResolving = resolvingRef.current.has(id)
              resolvingRef.current.add(id)
              if (hasNoDerivative(id)) {
                editFileRef.current(id, {
                  metadata: { derivativeResolved: true, derivativeReady: true },
                } as RecursivePartial<FileAPIEntry<Metadata>>)
                continue
              }
              for (const type of derivatives) {
                if (isResolving && type === displayDerivative) continue
                resolveVisualDerivative(sessionId, id, type).then((url) => {
                  if (type !== displayDerivative) return
                  editFileRef.current(id, {
                    displayUrl: url,
                    metadata: { derivativeResolved: true, derivativeReady: !!url },
                  } as RecursivePartial<FileAPIEntry<Metadata>>)
                })
              }
            }
          } else {
            // Plain-upload kind (no derivative): the uploaded file IS the final
            // artifact, so mark it ready on settle for the projection.
            for (const id of successIds) {
              editFileRef.current(id, {
                metadata: { derivativeResolved: true, derivativeReady: true },
              } as RecursivePartial<FileAPIEntry<Metadata>>)
            }
          }
        },
      })
    }, [fileApi, ctx, getSessionId, spawnWarningToast, t])

    const remove = useCallback((id: string) => {
      // `gcFilename` is the server-assigned name, set only once an upload has been
      // accepted. Without one there is no remote object to delete, and the request
      // would carry the LOCAL FileAPI id as the filename — which the backend can
      // only answer 404 to, marking the entry ERROR again and making it permanently
      // unremovable. Drop it locally instead. Seeded entries (draft resume) do carry
      // a gcFilename, so they still delete server-side.
      const entry = fileApi.allFilesArray.find((e) => e.id === id)
      if (entry && !entry.gcFilename) {
        fileApi.deleteFiles([id], { localOnly: true })
        return
      }
      fileApi.deleteFiles([id], { deleteHandlerFnc: kind.makeDeleteHandler(ctx) })
    }, [fileApi, ctx])

    // A stored file keeps the client's name only here, so it is recorded for the draft as soon as the upload lands.
    useEffect(() => {
      const names: Record<string, string> = {}
      for (const entry of fileApi.allFilesArray) {
        if (entry.state !== FileState.SUCCESS || !entry.gcFilename) continue
        const name = entry.originalFilename || entry.fileObject?.name
        if (name && name !== entry.gcFilename) names[entry.gcFilename] = name
      }
      if (Object.keys(names).length > 0) usePfStore.getState().rememberUploadNames(names)
    }, [fileApi.allFilesArray])

    /**
     * DISPLAY-ONLY seed of SUCCESS marker entries from persisted filenames
     * (Wave 1 — order-persisted kinds like PoA/certs). On resume the draft DTO
     * carries filenames but the regenerated session's hub projection is empty;
     * seeding renders the resumed files in the zone and lets `remove()` delete
     * them uniformly (the delete handler keys off `gcFilename ?? id` = the
     * filename). Wraps `mergeAddFiles` (idempotent per id), so it is safe to call
     * once on mount.
     *
     * ⚠️ `seed()` MUST NOT arm any module's projection→DTO sync — it flips no
     * `touched` flag (MODULE-CONTRACT §4b). Staging never calls it, so its
     * byte-identical gate stays clean.
     */
    const seed = useCallback((filenames: string[], tag?: string, displayNames?: Record<string, string>) => {
      if (!filenames.length) return
      const known = usePfStore.getState().uploadDisplayNames
      const displayNameOf = (name: string) => displayNames?.[name] ?? known[name] ?? name
      fileApi.mergeAddFiles(
        filenames.map((name) => ({
          id: name,
          // The client's own filename when the caller kept it (a resumed draft
          // does); the server name otherwise, which is at least stable.
          originalFilename: displayNameOf(name),
          // The listing row labels itself from `fileObject.name` (never from
          // `originalFilename`), so a seed without one renders an unnamed row.
          fileObject: new File([], displayNameOf(name)),
          gcFilename: name,
          // The grouping tag, so a seeded file lands in the same bucket the
          // upload would have: without it a resumed file is untagged and every
          // per-product reader filters it straight back out.
          tag,
          state: FileState.SUCCESS,
          action: FileAction.UPLOAD,
          progress: 100,
          // NOT resolved: seeding renders the row, it does not fetch anything. The
          // provider's resolve pass below fills `displayUrl` — claiming resolved
          // here is what left a resumed file with a blank thumbnail forever, since
          // the flag also tells that pass there is nothing to do.
          //
          // `derivativeReady` deliberately STAYS true: the file is on the session,
          // and the staging room-set reconcile prunes a room whose visual has left
          // the ready feed (`useStagingRoomSync`) — so flipping it would delete the
          // client's room instead of merely missing its thumbnail.
          metadata: { derivativeResolved: false, derivativeReady: true } as Metadata,
        } as Pick<FileAPIEntry<Metadata>, 'id'> & Partial<FileAPIEntry<Metadata>>))
      )
    }, [fileApi])

    /**
     * Fills in the display derivative for entries that still need it — in
     * practice the files a resumed line owns, put back on screen by `seed()`
     * (`useResumedUploads`). The upload path resolves on settle; a resumed file
     * never had a settle in this browser session, so without this pass its
     * `displayUrl` stayed null and the zone/staging room rendered a blank image.
     *
     * Runs at most once per id (`resolvingRef`), needs a session (the endpoint is
     * session-keyed), and marks the entry resolved either way so a derivative that
     * genuinely cannot be produced does not re-poll on every render. A failure
     * writes no `displayUrl` and does NOT clear `derivativeReady` — see the seed.
     */
    useEffect(() => {
      if (!kind.derivatives?.length) return
      const sessionId = getSessionId()
      if (!sessionId) return

      const displayDerivative = kind.displayDerivative ?? kind.derivatives[0]

      for (const entry of fileApi.allFilesArray) {
        const id = entry.gcFilename ?? entry.id
        if (entry.state !== FileState.SUCCESS) continue
        if (entry.action === FileAction.DELETE) continue
        if (entry.metadata?.derivativeResolved) continue
        if (resolvingRef.current.has(id)) continue

        resolvingRef.current.add(id)
        if (hasNoDerivative(id)) {
          editFileRef.current(id, { metadata: { derivativeResolved: true, derivativeReady: true } } as Parameters<typeof editFileRef.current>[1])
          continue
        }
        resolveVisualDerivative(sessionId, id, displayDerivative).then((url) => {
          editFileRef.current(id, (url
            ? { displayUrl: url, metadata: { derivativeResolved: true, derivativeReady: true } }
            : { metadata: { derivativeResolved: true } }
          ) as Parameters<typeof editFileRef.current>[1])
        })
      }
    }, [fileApi.allFilesArray, getSessionId])

    /** Drops the entries holding these stored filenames from the scope only, for files the session does not hold. */
    const discard = useCallback((filenames: readonly string[]) => {
      const ids = fileApi.allFilesArray
        .filter((entry) => entry.gcFilename && filenames.includes(entry.gcFilename))
        .map((entry) => entry.id)
      if (ids.length > 0) fileApi.deleteFiles(ids, { localOnly: true })
    }, [fileApi])

    const projected = useMemo(() => kind.project(fileApi.allFilesArray), [fileApi.allFilesArray])

    return {
      /** Raw FileAPI entries for a listing UI (real upload progress/errors). */
      entries: fileApi.allFilesArray,
      upload,
      remove,
      seed,
      discard,
      touchedTags,
      markTouched,
      /** Ids seen live in this scope's feed this session (§10.2 membership prune). */
      seenThisSession,
      ...projected,
    }
  })
}
